import { createSignal, onCleanup, onMount, Show } from 'solid-js';
import Text from '../components/Text';
import { formatTime } from '../services/dateFormat';

const PROBE_PATH = '/api/health';
const PROBE_INTERVAL_MS = 3000;
const PROBE_TIMEOUT_MS = 4000;

function readReturnUrl(): string {
    try {
        const params = new URLSearchParams(window.location.search);
        const r = params.get('return');
        if (r && r.startsWith('/')) return r;
    } catch { /* fallback below */ }
    return '/';
}

async function probe(): Promise<boolean> {
    const ctl = new AbortController();
    const t = window.setTimeout(() => ctl.abort(), PROBE_TIMEOUT_MS);
    try {
        const res = await fetch(PROBE_PATH, { signal: ctl.signal, headers: { Accept: 'application/json' } });
        return res.status >= 200 && res.status < 300;
    } catch {
        return false;
    } finally {
        window.clearTimeout(t);
    }
}

export default function Maintenance() {
    const returnUrl = readReturnUrl();
    const [checking, setChecking] = createSignal(false);
    const [lastChecked, setLastChecked] = createSignal<Date | null>(null);
    const [attempts, setAttempts] = createSignal(0);
    const [restored, setRestored] = createSignal(false);

    let intervalHandle: number | null = null;
    let cancelled = false;

    async function tick() {
        if (checking() || cancelled) return;
        setChecking(true);
        setAttempts((a) => a + 1);
        const ok = await probe();
        setLastChecked(new Date());
        setChecking(false);
        if (ok && !cancelled) {
            setRestored(true);
            if (intervalHandle != null) {
                window.clearInterval(intervalHandle);
                intervalHandle = null;
            }
            // Full navigate (not SPA route push) so every provider/page initialiser re-runs
            // against the now-healthy backend; spares us trying to recover SPA state mid-flight.
            window.setTimeout(() => { window.location.href = returnUrl; }, 600);
        }
    }

    onMount(() => {
        void tick();
        intervalHandle = window.setInterval(() => { void tick(); }, PROBE_INTERVAL_MS);
        onCleanup(() => {
            cancelled = true;
            if (intervalHandle != null) window.clearInterval(intervalHandle);
        });
    });

    return (
        <div class="v" style={{
            'min-height': '100dvh',
            'align-items': 'center',
            'justify-content': 'center',
            padding: 'var(--spacing-2xl)',
            gap: 'var(--spacing-xl)',
            'background': 'var(--dark-bg-primary)',
            color: 'var(--dark-text-primary)',
        }}>
            <div class="fcard v" style={{
                'max-width': '480px',
                'align-items': 'center',
                'text-align': 'center',
                padding: 'var(--spacing-3xl)',
                gap: 'var(--spacing-lg)',
            }}>
                <i class="fa-solid fa-screwdriver-wrench" style={{
                    'font-size': '3rem',
                    color: 'var(--warning-500)',
                }} />
                <h1 style={{ margin: 0, 'font-size': 'var(--font-size-2xl)' }}>
                    <Text val="MAINTENANCE_TITLE" />
                </h1>
                <p style={{ margin: 0, color: 'var(--dark-text-secondary)' }}>
                    <Text val="MAINTENANCE_BODY" />
                </p>

                <Show when={!restored()} fallback={
                    <p style={{ margin: 0, color: 'var(--success-500)', 'font-weight': 'var(--font-weight-semibold)' }}>
                        <i class="fa-solid fa-check" /> <Text val="MAINTENANCE_BACK_ONLINE" />
                    </p>
                }>
                    <div class="h" style={{ 'align-items': 'center', gap: 'var(--spacing-sm)', color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                        <Show when={checking()} fallback={
                            <>
                                <i class="fa-solid fa-circle-notch" style={{ opacity: 0.5 }} />
                                <span>
                                    <Text val="MAINTENANCE_NEXT_CHECK" />
                                </span>
                            </>
                        }>
                            <span class="spinner" style={{ width: '14px', height: '14px', 'border-width': '2px' }} />
                            <span><Text val="MAINTENANCE_CHECKING" /></span>
                        </Show>
                    </div>

                    <div style={{ color: 'var(--dark-text-muted)', 'font-family': 'var(--font-family-mono)', 'font-size': 'var(--font-size-xs)' }}>
                        <Text val="MAINTENANCE_ATTEMPTS" />: {attempts()}
                        <Show when={lastChecked()}>
                            {(d) => <> | {formatTime(d(), '--', true)}</>}
                        </Show>
                    </div>

                    <button class="btn btn-secondary" onClick={() => void tick()} disabled={checking()}>
                        <i class="fa-solid fa-rotate" /> <Text val="MAINTENANCE_RETRY_NOW" />
                    </button>
                </Show>

                <div style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                    <Text val="MAINTENANCE_RETURN_TO" />: <code style={{ 'font-family': 'var(--font-family-mono)' }}>{returnUrl}</code>
                </div>
            </div>
        </div>
    );
}
